<template>
  <div class="card p-6 m-6" dir="rtl">
    <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
      <p class="card-title m-0">שיחות צ'אטבוט</p>
      <router-link :to="{ name: 'system' }" class="button is-light">חזרה</router-link>
    </div>

    <!-- Tabs -->
    <div class="tabs is-boxed mb-4">
      <ul>
        <li :class="{ 'is-active': activeTab === 'convSvc' }">
          <a @click="activeTab = 'convSvc'; fetchConvSvcConversations()">שיחות WhatsApp (שירות חדש)</a>
        </li>
        <li :class="{ 'is-active': activeTab === 'conversations' }">
          <a @click="activeTab = 'conversations'">שיחות פעילות</a>
        </li>
        <li :class="{ 'is-active': activeTab === 'strong' }">
          <a @click="activeTab = 'strong'; fetchStrongOffers()">הצעות חזקות</a>
        </li>
      </ul>
    </div>

    <!-- Tab: Conv-Svc Conversations -->
    <div v-show="activeTab === 'convSvc'">
      <div class="card-content">
        <!-- Filters -->
        <div class="is-flex mb-4" style="gap: 10px;">
          <div class="select is-small">
            <select v-model="convSvcStateFilter" @change="convSvcPage = 1; fetchConvSvcConversations()">
              <option value="">כל הסטטוסים</option>
              <option value="pending_opening">ממתין לפתיחה</option>
              <option value="awaiting_consent">ממתין להסכמה</option>
              <option value="open">פתוח</option>
              <option value="ready_for_handoff">מוכן למסירה</option>
              <option value="escalated_to_human">הועבר לגורם אנושי</option>
              <option value="auto_closed_no_response">נסגר אוטומטית</option>
              <option value="completed">הושלם</option>
              <option value="paused">מושהה</option>
              <option value="opted_out">ביטל הסכמה</option>
            </select>
          </div>
          <input
            class="input is-small"
            type="search"
            v-model="convSvcSearch"
            placeholder="חפש שם או טלפון..."
            style="max-width: 250px;"
            @keyup.enter="convSvcPage = 1; fetchConvSvcConversations()"
          />
          <button class="button is-small is-info" @click="convSvcPage = 1; fetchConvSvcConversations()">חפש</button>
        </div>

        <div v-if="convSvcLoading" class="has-text-centered py-6">
          <p>טוען נתונים...</p>
        </div>
        <div v-else-if="convSvcConversations.length > 0">
          <Pagination
            v-if="convSvcMeta"
            :totalPages="convSvcMeta.total_pages"
            :currentPage="convSvcMeta.current_page"
            @page-change="handleConvSvcPageChange"
            @per-page-change="handleConvSvcPerPageChange"
          />
          <p class="is-size-7 has-text-grey mb-3">סה"כ {{ convSvcMeta.total_count }} שיחות</p>
          <table class="table is-fullwidth is-striped is-hoverable mt-2">
            <thead>
              <tr>
                <th style="width: 3%;"></th>
                <th style="width: 17%;">מועמד</th>
                <th style="width: 12%;">טלפון</th>
                <th style="width: 12%;" class="has-text-centered">סטטוס</th>
                <th style="width: 8%;" class="has-text-centered">ציון</th>
                <th style="width: 8%;" class="has-text-centered">הודעות</th>
                <th style="width: 13%;">התחלה</th>
                <th style="width: 13%;">פעילות אחרונה</th>
                <th style="width: 14%;" class="has-text-centered">ממשק בדיקה</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="conv in convSvcConversations">
                <tr :key="conv.id" @click="toggleConvSvcExpand(conv.id)" style="cursor: pointer;">
                  <td>
                    <span class="icon is-small">
                      <i :class="convSvcExpandedId === conv.id ? 'fas fa-chevron-down' : 'fas fa-chevron-left'"></i>
                    </span>
                  </td>
                  <td>
                    <template v-if="conv.rebase_applicant_id">
                      <LinkEntity :payload="convSvcApplicantPayload(conv)" />
                    </template>
                    <template v-else>{{ conv.applicant_name || '---' }}</template>
                  </td>
                  <td class="is-size-7">
                    <a
                      v-if="conv.rebase_applicant_id && conv.applicant_phone"
                      href="#"
                      class="blue_text"
                      @click.prevent="openApplicantModal(conv, 'click')"
                      @mouseover="openApplicantModal(conv, 'hover')"
                      @mouseleave="closeApplicantModal"
                    >{{ conv.applicant_phone }}</a>
                    <span v-else>{{ conv.applicant_phone || '---' }}</span>
                  </td>
                  <td class="has-text-centered">
                    <span :class="['tag', convSvcStateClass(conv.state)]">
                      {{ convSvcStateLabel(conv.state) }}
                    </span>
                  </td>
                  <td class="has-text-centered">
                    <span class="tag is-light" :class="convSvcScoreClass(conv.completeness_score)">
                      {{ Number(conv.completeness_score).toFixed(0) }}%
                    </span>
                  </td>
                  <td class="has-text-centered">{{ conv.message_count }}</td>
                  <td class="is-size-7">{{ $moment(conv.started_at).format('DD/MM/YY HH:mm') }}</td>
                  <td class="is-size-7">{{ $moment(conv.last_event_at).format('DD/MM/YY HH:mm') }}</td>
                  <td class="has-text-centered" @click.stop>
                    <a
                      v-if="conv.rebase_applicant_id"
                      :href="chatbotPanelUrl(conv.rebase_applicant_id)"
                      target="_blank"
                      class="button is-small is-info"
                      title="ממשק בדיקה"
                    >🤖</a>
                  </td>
                </tr>
                <!-- Expanded messages row -->
                <tr v-if="convSvcExpandedId === conv.id" :key="conv.id + '-messages'">
                  <td colspan="9" class="p-0">
                    <div class="conv-messages-container">
                      <div v-if="convSvcMessagesLoading" class="has-text-centered py-4">
                        <p class="is-size-7">טוען הודעות...</p>
                      </div>
                      <div v-else-if="convSvcMessages[conv.id] && convSvcMessages[conv.id].length > 0" class="messages-list">
                        <div
                          v-for="msg in convSvcMessages[conv.id]"
                          :key="msg.id"
                          :class="['message-bubble', msg.direction === 'in' ? 'inbound' : 'outbound']"
                        >
                          <div class="message-content">{{ msg.content }}</div>
                          <div class="message-meta">
                            {{ $moment(msg.received_at || msg.sent_at).format('DD/MM HH:mm') }}
                            <span v-if="msg.classification" class="ml-2 tag is-small is-light">{{ msg.classification }}</span>
                            <span v-if="msg.llm_model" class="ml-2 is-size-8 has-text-grey">{{ msg.llm_model }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-else class="has-text-centered py-4">
                        <p class="is-size-7 has-text-grey">אין הודעות</p>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-else class="has-text-centered py-6">
          <p>לא נמצאו שיחות</p>
        </div>
      </div>
    </div>

    <!-- Tab: Active Conversations -->
    <div v-show="activeTab === 'conversations'">
      <!-- Chart Section -->
      <div class="card-content mb-5">
        <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
          <p class="is-size-5 has-text-weight-semibold">ציוני שיחות צ'אטבוט לפי ימים</p>
          <div class="is-flex" style="gap: 8px;">
            <div class="select is-small">
              <select v-model="statsDays" @change="fetchStats">
                <option :value="7">7 ימים</option>
                <option :value="14">14 ימים</option>
                <option :value="30">30 ימים</option>
                <option :value="60">60 ימים</option>
                <option :value="90">90 ימים</option>
              </select>
            </div>
          </div>
        </div>
        <div v-if="statsLoading" class="has-text-centered py-5">
          <p>טוען סטטיסטיקות...</p>
        </div>
        <div v-else-if="statsData.length > 0">
          <apexchart
            type="bar"
            height="370"
            :options="chartOptions"
            :series="chartSeries"
          />
        </div>
        <div v-else class="has-text-centered py-5">
          <p>אין נתונים לתקופה הנבחרת</p>
        </div>
      </div>

      <!-- Table Section -->
      <div class="card-content">
        <div class="mb-4">
          <input class="input" type="search" v-model="search" placeholder="חפש מועמד..." style="max-width: 300px;" />
        </div>
        <div v-if="loading" class="has-text-centered py-6">
          <p>טוען נתונים...</p>
        </div>
        <div v-else-if="filteredConversations.length > 0">
          <Pagination
            v-if="paginationMeta"
            :totalPages="paginationMeta.total_pages"
            :currentPage="paginationMeta.current_page"
            @page-change="handlePageChange"
            @per-page-change="handlePerPageChange"
          />
          <table class="table is-fullwidth is-striped is-hoverable mt-4">
            <thead>
              <tr>
                <th style="width: 22%;">מועמד</th>
                <th style="width: 10%;" class="has-text-centered">מבצע</th>
                <th style="width: 38%;">הודעה אחרונה</th>
                <th style="width: 18%;">תאריך</th>
                <th style="width: 12%;" class="has-text-centered">ממשק בדיקה</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="conv in filteredConversations" :key="conv.applicant_id">
                <td>
                  <LinkEntity :payload="applicantPayload(conv)" />
                </td>
                <td class="has-text-centered">
                  <span :class="['tag', conv.owner === 'agent' ? 'is-success' : 'is-info']">
                    {{ conv.owner === 'agent' ? '🤖 Agent' : '⚙️ Service' }}
                  </span>
                </td>
                <td>
                  <span class="conversation-content">{{ conv.last_message }}</span>
                </td>
                <td>
                  <span class="is-size-7">
                    {{ $moment(conv.last_message_at).format('DD/MM/YYYY HH:mm') }}
                  </span>
                </td>
                <td class="has-text-centered">
                  <a
                    :href="chatbotPanelUrl(conv.applicant_id)"
                    target="_blank"
                    class="button is-small is-info"
                    title="ממשק בדיקה"
                  >
                    🤖
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="has-text-centered py-6">
          <p>לא נמצאו שיחות</p>
        </div>
      </div>
    </div>

    <!-- Tab: Strong Offers -->
    <div v-show="activeTab === 'strong'">
      <div class="card-content">
        <div v-if="strongLoading" class="has-text-centered py-6">
          <p>טוען הצעות חזקות...</p>
        </div>
        <div v-else-if="strongOffers.length > 0">
          <Pagination
            v-if="strongMeta"
            :totalPages="strongMeta.total_pages"
            :currentPage="strongMeta.current_page"
            @page-change="handleStrongPageChange"
            @per-page-change="handleStrongPerPageChange"
          />
          <p class="is-size-7 has-text-grey mb-3">סה"כ {{ strongMeta.total_count }} הצעות חזקות</p>
          <div v-for="group in strongOffers" :key="group.applicant_id" class="box mb-4">
            <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
              <LinkEntity :payload="strongApplicantPayload(group)" />
              <span class="tag is-success is-light">{{ group.offers.length }} הצעות</span>
            </div>
            <table class="table is-fullwidth is-narrow is-hoverable">
              <thead>
                <tr>
                  <th>משרה</th>
                  <th class="has-text-centered" style="width: 100px;">ציון</th>
                  <th class="has-text-centered" style="width: 150px;">סטטוס נוכחי</th>
                  <th style="width: 160px;">תאריך הפיכה לחזקה</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="offer in group.offers" :key="offer.offer_id">
                  <td>{{ offer.position_title }}</td>
                  <td class="has-text-centered">
                    <span class="tag is-info is-light">{{ offer.score }}</span>
                  </td>
                  <td class="has-text-centered">
                    <OfferStatus :payload="{ status: offer.current_status }" buttonClass="button is-small is-rounded" />
                  </td>
                  <td>
                    <span class="is-size-7">{{ $moment(offer.strong_at).format('DD/MM/YYYY HH:mm') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="has-text-centered py-6">
          <p>לא נמצאו הצעות חזקות</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import Pagination from '@/components/shared/organisms/Pagination'
import LinkEntity from '@/components/LinkEntity.vue'
import OfferStatus from '@/components/OfferStatus.vue'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'ChatbotConversations',
  components: {
    Pagination,
    LinkEntity,
    OfferStatus,
    apexchart: VueApexCharts
  },
  data () {
    return {
      activeTab: 'convSvc',
      // Conversations tab
      conversations: [],
      search: '',
      loading: false,
      page: 1,
      limit: 50,
      paginationMeta: null,
      statsData: [],
      statsLoading: false,
      statsDays: 30,
      // Strong offers tab
      strongOffers: [],
      strongLoading: false,
      strongPage: 1,
      strongLimit: 50,
      strongMeta: null,
      strongFetched: false,
      // Conv-svc tab
      convSvcConversations: [],
      convSvcLoading: false,
      convSvcPage: 1,
      convSvcLimit: 50,
      convSvcMeta: null,
      convSvcStateFilter: '',
      convSvcSearch: '',
      convSvcExpandedId: null,
      convSvcMessages: {},
      convSvcMessagesLoading: false,
      phoneModalFixed: false
    }
  },
  computed: {
    ...mapGetters('modals', ['isModalOpen', 'findModal']),
    filteredConversations () {
      if (!this.search) return this.conversations
      return this.conversations.filter(c =>
        c.full_name.toLowerCase().includes(this.search.toLowerCase())
      )
    },
    chartSeries () {
      return [
        {
          name: 'מצוין (80-100)',
          type: 'bar',
          data: this.statsData.map(d => d.excellent)
        },
        {
          name: 'טוב (60-79)',
          type: 'bar',
          data: this.statsData.map(d => d.good)
        },
        {
          name: 'בינוני (40-59)',
          type: 'bar',
          data: this.statsData.map(d => d.fair)
        },
        {
          name: 'חלש (1-39)',
          type: 'bar',
          data: this.statsData.map(d => d.poor)
        },
        {
          name: 'ציון ממוצע',
          type: 'line',
          data: this.statsData.map(d => d.avg_score)
        }
      ]
    },
    chartOptions () {
      return {
        chart: {
          stacked: true,
          toolbar: { show: true },
          fontFamily: 'inherit'
        },
        plotOptions: {
          bar: {
            borderRadius: 3,
            columnWidth: '60%'
          }
        },
        colors: ['#48c774', '#3298dc', '#ffdd57', '#f14668', '#7a54d4'],
        stroke: {
          width: [0, 0, 0, 0, 3],
          curve: 'smooth'
        },
        xaxis: {
          categories: this.statsData.map(d => {
            const parts = d.date.split('-')
            return parts[2] + '/' + parts[1]
          }),
          labels: {
            rotate: -45,
            style: { fontSize: '11px' }
          }
        },
        yaxis: [
          {
            title: { text: 'מספר שיחות' },
            forceNiceScale: true
          },
          {
            opposite: true,
            title: { text: 'ציון ממוצע' },
            min: 0,
            max: 100,
            forceNiceScale: true
          }
        ],
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          fontSize: '13px'
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (val, { seriesIndex }) {
              if (seriesIndex === 4) return val + ' ציון'
              return val + ' שיחות'
            }
          }
        },
        dataLabels: {
          enabled: false
        }
      }
    }
  },
  created () {
    this.fetchConvSvcConversations()
    this.fetchConversations()
    this.fetchStats()
  },
  methods: {
    // The readonly chatbot panel proxies /admin/v1/ per operation slug, so the
    // link must carry this tenant's slug — without it the panel always queries
    // the rebase API (see chatbot_test_panel/src/services/api.js).
    chatbotPanelUrl (applicantId) {
      const slug = this.$settings.get({ path: 'intake_operation_slug' })
      let url = `https://chatbot.test.panel.hasama.online/?applicant_id=${applicantId}&readonly=true`
      if (slug) url += `&operation=${slug}`
      return url
    },
    applicantPayload (conv) {
      const nameParts = (conv.full_name || '').split(' ')
      return {
        resource_type: 'Applicant',
        resource_id: conv.applicant_id,
        first_name: conv.first_name || nameParts[0] || '',
        last_name: conv.last_name || nameParts.slice(1).join(' ') || '',
        full_name: conv.full_name,
        phone_number: conv.phone_number || ''
      }
    },
    strongApplicantPayload (group) {
      const nameParts = (group.full_name || '').split(' ')
      return {
        resource_type: 'Applicant',
        resource_id: group.applicant_id,
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' ') || '',
        full_name: group.full_name,
        phone_number: ''
      }
    },
    convSvcApplicantPayload (conv) {
      const nameParts = (conv.applicant_name || '').split(' ')
      return {
        resource_type: 'Applicant',
        resource_id: conv.rebase_applicant_id,
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' ') || '',
        full_name: conv.applicant_name || '',
        phone_number: conv.applicant_phone || ''
      }
    },
    convSvcStateLabel (state) {
      const labels = {
        pending_opening: 'ממתין לפתיחה',
        awaiting_consent: 'ממתין להסכמה',
        open: 'פתוח',
        ready_for_handoff: 'מוכן למסירה',
        escalated_to_human: 'הועבר לאנושי',
        auto_closed_no_response: 'נסגר אוטומטית',
        completed: 'הושלם',
        paused: 'מושהה',
        opted_out: 'ביטל הסכמה'
      }
      return labels[state] || state
    },
    convSvcStateClass (state) {
      const classes = {
        open: 'is-success',
        pending_opening: 'is-warning',
        awaiting_consent: 'is-warning',
        ready_for_handoff: 'is-info',
        completed: 'is-info',
        escalated_to_human: 'is-danger',
        auto_closed_no_response: 'is-light',
        opted_out: 'is-light',
        paused: 'is-warning is-light'
      }
      return classes[state] || 'is-light'
    },
    convSvcScoreClass (score) {
      const s = Number(score)
      if (s >= 80) return 'is-success'
      if (s >= 60) return 'is-info'
      if (s >= 40) return 'is-warning'
      return ''
    },
    async fetchConvSvcConversations () {
      this.convSvcLoading = true
      try {
        const params = {
          page: this.convSvcPage,
          per_page: this.convSvcLimit,
          // Tenant scope. Omitting it is how a rebase applicant reached this
          // very screen on suitup: conv-svc used to answer an operation-less
          // request with every tenant's conversations merged into one list.
          // Rebase re-injects its own slug server-side and conv-svc now rejects
          // a request without one — this is the client-side half of that fix,
          // and it matches ChatbotConversationModal.vue.
          operation: this.$settings.get({ path: 'intake_operation_slug' }) || undefined
        }
        if (this.convSvcStateFilter) params.state = this.convSvcStateFilter
        if (this.convSvcSearch) params.search = this.convSvcSearch
        const data = await this.$network_utils.getConvSvcConversations(params)
        this.convSvcConversations = data.data
        this.convSvcMeta = data.meta
      } catch (e) {
        console.error('Failed to fetch conv-svc conversations', e)
      } finally {
        this.convSvcLoading = false
      }
    },
    async toggleConvSvcExpand (conversationId) {
      if (this.convSvcExpandedId === conversationId) {
        this.convSvcExpandedId = null
        return
      }
      this.convSvcExpandedId = conversationId
      if (!this.convSvcMessages[conversationId]) {
        this.convSvcMessagesLoading = true
        try {
          const data = await this.$network_utils.getConvSvcMessages(conversationId)
          this.$set(this.convSvcMessages, conversationId, data.messages)
        } catch (e) {
          console.error('Failed to fetch messages', e)
        } finally {
          this.convSvcMessagesLoading = false
        }
      }
    },
    handleConvSvcPageChange (v) {
      this.convSvcPage = v
      this.convSvcExpandedId = null
      this.fetchConvSvcConversations()
    },
    handleConvSvcPerPageChange (v) {
      this.convSvcLimit = v
      this.convSvcPage = 1
      this.convSvcExpandedId = null
      this.fetchConvSvcConversations()
    },
    async fetchConversations () {
      this.loading = true
      try {
        const data = await this.$network_utils.getChatbotActiveConversations({
          page: this.page,
          per_page: this.limit
        })
        this.conversations = data.data
        this.paginationMeta = data.meta
      } catch (e) {
        console.error('Failed to fetch chatbot conversations', e)
      } finally {
        this.loading = false
      }
    },
    async fetchStats () {
      this.statsLoading = true
      try {
        const params = { days: this.statsDays }
        const data = await this.$network_utils.getChatbotConversationStats(params)
        this.statsData = data.stats
      } catch (e) {
        console.error('Failed to fetch conversation stats', e)
      } finally {
        this.statsLoading = false
      }
    },
    async fetchStrongOffers () {
      if (this.strongFetched && this.strongOffers.length > 0) return
      this.strongLoading = true
      try {
        const data = await this.$network_utils.getChatbotStrongOffers({
          page: this.strongPage,
          per_page: this.strongLimit
        })
        this.strongOffers = data.data
        this.strongMeta = data.meta
        this.strongFetched = true
      } catch (e) {
        console.error('Failed to fetch strong offers', e)
      } finally {
        this.strongLoading = false
      }
    },
    handlePageChange (v) {
      this.page = v
      this.fetchConversations()
    },
    handlePerPageChange (v) {
      this.limit = v
      this.page = 1
      this.fetchConversations()
    },
    handleStrongPageChange (v) {
      this.strongPage = v
      this.strongFetched = false
      this.fetchStrongOffers()
    },
    handleStrongPerPageChange (v) {
      this.strongLimit = v
      this.strongPage = 1
      this.strongFetched = false
      this.fetchStrongOffers()
    },
    openApplicantModal (conv, action) {
      if (action === 'hover') this.phoneModalFixed = false
      if (action === 'click') this.phoneModalFixed = true
      const payload = this.convSvcApplicantPayload(conv)
      this.createModal({
        designator: 'link-entity-resource-modal',
        resource_type: 'Applicant',
        resource_id: conv.rebase_applicant_id,
        component_name: 'ModalApplicant',
        data: payload,
        modal_title: `${payload.full_name} | ${conv.applicant_phone} | ${conv.rebase_applicant_id}`,
        modal_width: action === 'click' ? 1500 : undefined,
        is_center_modal: action === 'click',
        modal_open_action: action
      })
    },
    closeApplicantModal () {
      if (this.phoneModalFixed) return
      const modal = this.findModal({ type: 'designator', value: 'link-entity-resource-modal' })
      if (modal) this.removeModal(modal)
    },
    ...mapMutations('modals', { createModal: 'create', removeModal: 'remove' })
  }
}
</script>

<style scoped>
.conversation-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
  display: block;
  font-size: 14px;
}
.conv-messages-container {
  background: #fafafa;
  border-top: 1px solid #eee;
  padding: 16px 24px;
  max-height: 500px;
  overflow-y: auto;
}
.messages-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.message-bubble {
  padding: 8px 12px;
  border-radius: 10px;
  max-width: 70%;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}
.message-bubble.inbound {
  background-color: #e3f2fd;
  align-self: flex-end;
}
.message-bubble.outbound {
  background-color: #e8f5e9;
  align-self: flex-start;
}
.message-content {
  white-space: pre-wrap;
}
.message-meta {
  font-size: 11px;
  color: #888;
  margin-top: 3px;
}
</style>
